import "./Chat.scss";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import EmojiPicker from "emoji-picker-react";
import SmileIcon from "@mui/icons-material/SentimentSatisfiedOutlined";
import SendBtn from "./../../assets/send-icon.png";
import User from "./../../assets/avatar.svg";
import api from "../../utils/api";
import { message } from "antd";
import { CircularProgress } from "@mui/material";

// Helper function to clean and format the date
const formatMessageTime = (dateString) => {
  if (!dateString) return "";

  try {
    // Remove am/pm from the end and clean the string
    let cleanDate = dateString.replace(/\s+(am|pm)$/i, "");

    // Parse the cleaned date
    const date = moment(cleanDate);

    if (!date.isValid()) {
      // If still invalid, try parsing without seconds
      const dateWithoutSeconds = cleanDate.replace(/:\d{2}$/, "");
      const fallbackDate = moment(dateWithoutSeconds);
      return fallbackDate.isValid()
        ? fallbackDate.format("a hh:mm")
        : "وقت غير صحيح";
    }

    return date.format("a hh:mm");
  } catch (error) {
    console.error("Error formatting date:", error);
    return "وقت غير صحيح";
  }
};

const Message = ({ message, isOwnMessage }) => (
  <div className={isOwnMessage ? "message own" : "message"}>
    {isOwnMessage && (
      <div className="message-user">
        <img src={User} alt="" />
        <p className="user-name">
          {message.sender_type === "admin" ? "الدعم الفني" : "العميل"}
        </p>
      </div>
    )}
    <div className="texts">
      <p>{message.message}</p>
      <span>{formatMessageTime(message.created_at)}</span>
    </div>
  </div>
);

const Chat = ({ selectedRow, selectedId }) => {
  const [messages, setMessages] = useState([]);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch messages when component mounts or selectedId changes
  useEffect(() => {
    if (selectedId) {
      fetchMessages();
    }
  }, [selectedId]);

  // Auto scroll to bottom when messages update
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      adjustTextareaHeight();
    }
  }, [text]);

  const adjustTextareaHeight = () => {
    const textarea = inputRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const scrollHeight = textarea.scrollHeight;
      const maxHeight = 300; // Maximum height in pixels
      const minHeight = 60; // Minimum height to match original design

      if (scrollHeight > maxHeight) {
        textarea.style.height = `${maxHeight}px`;
        textarea.style.overflowY = "scroll";
      } else {
        textarea.style.height = `${Math.max(scrollHeight, minHeight)}px`;
        textarea.style.overflowY = "hidden";
      }
    }
  };

  console.log(selectedId);

  const fetchMessages = async () => {
    if (!selectedId) return;

    setLoading(true);
    try {
      const response = await api.get(
        `/support_messages?support_chat_id=${selectedId}`
      );
      setMessages(response.data.data || []);
    } catch (error) {
      console.error("Error fetching messages:", error);
      message.error("حدث خطأ في تحميل الرسائل");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!text.trim() || !selectedId || sending) return;

    setSending(true);
    try {
      const messageData = {
        message: text.trim(),
        support_chat_id: selectedId,
        sender_type: "admin",
      };

      const response = await api.post("/support_messages", messageData);

      // Add the new message to the messages array
      setMessages((prev) => [...prev, response.data.data]);
      setText("");

      // Focus back to input
      inputRef.current?.focus();
    } catch (error) {
      console.error("Error sending message:", error);
      message.error("حدث خطأ في إرسال الرسالة");
    } finally {
      setSending(false);
    }
  };

  const handleEmoji = (e) => {
    setText((prev) => prev + e.emoji);
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    // Allow Shift + Enter for new lines, Enter alone sends message
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
    // Shift + Enter will naturally create a new line in textarea
  };

  const handleTextChange = (e) => {
    setText(e.target.value);
  };

  if (!selectedRow) {
    return (
      <div className="chat-container">
        <div className="no-chat-selected">
          <p>لم يتم اختيار محادثة</p>
        </div>
      </div>
    );
  }

  const hasMessages = messages.length > 0;

  return (
    <div className="chat-container">
      <div className="chat-header">
        <p className={!hasMessages ? "no-messages-text" : ""}>
          {selectedRow?.support_question || "لا يوجد موضوع للمحادثة"}
        </p>
      </div>

      <div className="center">
        {loading ? (
          <div className="loading-container">
            <CircularProgress size={30} />
            <p>جاري تحميل الرسائل...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="no-messages">
            <p>لا توجد رسائل في هذه المحادثة</p>
          </div>
        ) : (
          messages.map((msg, index) => (
            <Message
              key={msg.id || index}
              message={msg}
              isOwnMessage={msg.sender_type === "admin"}
            />
          ))
        )}
        <div ref={endRef}></div>
      </div>

      <form onSubmit={handleSendMessage} className="bottom">
        <textarea
          placeholder="اكتب رسالتك..."
          onChange={handleTextChange}
          onKeyPress={handleKeyPress}
          value={text}
          ref={inputRef}
          disabled={sending}
          rows={1}
        />
        <div className="emoji">
          <SmileIcon
            onClick={() => setOpen((prev) => !prev)}
            aria-label="Open emoji picker"
            className="icon"
            style={{ cursor: "pointer" }}
          />
          <div className="picker">
            <EmojiPicker open={open} onEmojiClick={handleEmoji} />
          </div>
        </div>
        <button
          type="submit"
          className="sendButton"
          disabled={!text.trim() || sending}
        >
          {sending ? (
            <CircularProgress size={20} />
          ) : (
            <img src={SendBtn} alt="Send" />
          )}
        </button>
      </form>
    </div>
  );
};

export default Chat;
